import { useState, useCallback, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { ReducersTypes } from "@/redux/reducers/Models";
import { Card } from "../Models/index";
import { setCustomer } from "@/redux/ducks/customer";
import { getMaskedCardNumber } from "@/utils/transform";
import useCybersourceService from "@/hooks/useCybersourceService";
import { PAYMENT_MODE, useChangePaymentMethod } from "@/hooks/usePaymentMethod";
import { useSingleCardBrand } from "@/hooks/useCardBrands";

export function useCards() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const customer = useSelector((state: ReducersTypes) => state.customerReducer);
  const { brand, isLoading } = useSingleCardBrand(
    customer?.account?.card?.id || "",
  );
  const { addCyberSourceScripts, getSessionId } = useCybersourceService();
  const visitorId = getSessionId() ?? "";

  const {
    handleChangePaymentMethod: handleChangePaymentMethodBoleto,
    isLoading: isChangePaymentMethodLoading,
    isSuccess: isChangePaymentMethodSuccess,
    error: changePaymentMethodError,
  } = useChangePaymentMethod(
    visitorId,
    PAYMENT_MODE.BOLETO_PRE,
    customer?.account,
  );

  const {
    handleChangePaymentMethod: handleChangePaymentMethodPrePago,
    isLoading: isChangeCardPrePagoLoading,
    error: changeCardPrePagoError,
  } = useChangePaymentMethod(
    visitorId,
    PAYMENT_MODE.PRE_PAGO_FULL,
    customer?.account,
  );

  const {
    handleChangePaymentMethod: handleChangePaymentMethodDebito,
    isLoading: isChangeCardDebitoLoading,
    error: changeCardDebitoError,
  } = useChangePaymentMethod(
    visitorId,
    PAYMENT_MODE.DEBITO_POSTECIPADO,
    customer?.account,
  );

  const handleChangePaymentMethodCard = useCallback(
    async (cardData: any) => {
      if (customer?.account?.card && cardData?.number) {
        const creditCard = {
          flagCode: customer.account.card.id || "",
          expirationMonth: customer.account.card.dueMonth || "",
          expirationYear: customer.account.card.dueYear || "",
          cardNumber: cardData.number,
        };

        if (cardData.autoPayment) {
          await handleChangePaymentMethodPrePago(creditCard);
        } else {
          await handleChangePaymentMethodDebito(creditCard);
        }
      }
    },
    [
      customer,
      handleChangePaymentMethodPrePago,
      handleChangePaymentMethodDebito,
    ],
  );

  const [isShowingMenu, setIsShowingMenu] = useState(false);
  const [isVisibleActionCardModal, setIsVisibleActionCardModal] =
    useState(false);
  const [isVisibleConfirmationModal, setIsVisibleConfirmationModal] =
    useState(false);
  const [isVisibleValidationModal, setIsVisibleValidationModal] =
    useState(false);
  const [
    isVisiblePaymentConfirmationModal,
    setIsVisiblePaymentConfirmationModal,
  ] = useState(false);
  const [selectedCard, setSelectedCard] = useState<Card | null>(null);
  const [error, setError] = useState(false);

  const cards = useMemo(
    () =>
      ((customer?.account?.card?.id && [
        {
          brand: brand?.descricaoBandeira || "",
          number: getMaskedCardNumber(customer?.account?.card?.cardNumber || ""),
          name: customer?.register?.name || "",
          expiration: customer?.account?.card?.dueMonth
            ? `${customer?.account?.card?.dueMonth}/${customer?.account?.card?.dueYear}`
            : "",
          autoPayment: customer?.account?.paymentMode === "2" || false,
        },
      ]) ||
        []) as Card[],
    [
      brand?.descricaoBandeira,
      customer?.account?.card?.id,
      customer?.account?.card?.cardNumber,
      customer?.account?.card?.dueMonth,
      customer?.account?.card?.dueYear,
      customer?.account?.paymentMode,
      customer?.register?.name,
    ],
  );

  const toggleMenu = useCallback(() => setIsShowingMenu((prev) => !prev), []);

  const toggleActionCardModal = useCallback(
    () => setIsVisibleActionCardModal((prev) => !prev),
    [],
  );

  const toggleConfirmationModal = useCallback(
    () => setIsVisibleConfirmationModal((prev) => !prev),
    [],
  );

  const toggleValidationModal = useCallback(
    () => setIsVisibleValidationModal((prev) => !prev),
    [],
  );

  const togglePaymentConfirmationModal = useCallback(
    () => setIsVisiblePaymentConfirmationModal((prev) => !prev),
    [],
  );

  const toggleCardSelection = useCallback(
    (idx: number) => setSelectedCard(cards[idx]),
    [cards],
  );

  const handleToggleAutoPayment = useCallback(
    (cardIndex: number) => {
      setSelectedCard(cards[cardIndex]);
      toggleValidationModal();
    },
    [cards, toggleValidationModal],
  );

  const goBack = useCallback(() => {
    navigate("..", { relative: "path" });
  }, [navigate]);

  const goToNewCard = () => navigate("/newcard");

  useEffect(() => {
    if (changeCardDebitoError || changeCardPrePagoError) {
      setError(true);
    }
  }, [changeCardDebitoError, changeCardPrePagoError]);

  useEffect(() => {
    if (customer?.account?.card?.id && !customer?.account?.card?.brand) {
      dispatch(
        setCustomer({
          ...customer,
          account: {
            ...customer?.account,
            card: {
              ...customer?.account?.card,
              brand: brand?.descricaoBandeira || "N/A",
            },
          },
        }),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customer, brand]);

  useEffect(() => {
    if (!changePaymentMethodError && changePaymentMethodError != null) {
      setError(true);
      return;
    }

    if (
      !changePaymentMethodError &&
      !isChangePaymentMethodLoading &&
      isChangePaymentMethodSuccess
    ) {
      dispatch(
        setCustomer({
          ...customer,
          account: {
            ...customer?.account,
            card: {},
          },
        }),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isChangePaymentMethodLoading,
    isChangePaymentMethodSuccess,
    changePaymentMethodError,
  ]);

  useEffect(() => {
    addCyberSourceScripts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    goBack,
    goToNewCard,
    cards,
    selectedCard,
    toggleCardSelection,
    isShowingMenu,
    toggleMenu,
    isVisibleActionCardModal,
    toggleActionCardModal,
    isVisibleConfirmationModal,
    toggleConfirmationModal,
    isVisibleValidationModal,
    toggleValidationModal,
    isVisiblePaymentConfirmationModal,
    togglePaymentConfirmationModal,
    handleToggleAutoPayment,
    handleDeleteCard: handleChangePaymentMethodBoleto,
    handleChangePaymentMethodCard: handleChangePaymentMethodCard,
    isLoading:
      isChangePaymentMethodLoading ||
      isChangeCardPrePagoLoading ||
      isChangeCardDebitoLoading ||
      isLoading,
    error,
    setError,
  };
}
